// Ports prototype/portal-signin.jsx. Inline styles + classes preserved per
// design_handoff_rev9_portal/starter/CLAUDE.md.

/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useRef, type FormEvent } from 'react';

import { EntityDisclaimer } from '../../components/EntityDisclaimer';
import { useLocation, useNavigate } from 'react-router-dom';

import { Brand, Kicker } from '../../components/shell';
import { useAuth } from '../../lib/auth';

declare const google: any;

// GSI's `google.accounts.id` is a page-lifetime singleton — it survives every
// SPA remount of SignIn (sign-out -> sign-in again, etc). initialize() must
// only ever run once per page load or GSI logs "called multiple times" and
// silently drops the earlier config.
let googleInitialized = false;

// Committed fallback so the button renders on every box without depending on a
// build-time env file existing there (Vite reads env files only from web/ —
// the repo-root .env is Django's, and web/.env.local is gitignored, so a box
// that never created it shipped a bundle with the whole Google block minified
// away). An OAuth *client ID* is public by design — it ships in the bundle of
// every Google Sign-In site; the gate is Google's Authorized JavaScript
// origins list plus backend token verification, not secrecy. The client
// SECRET is the sensitive half and this app never uses it.
const GOOGLE_CLIENT_ID_FALLBACK =
  '895111627173-8kfp9h7fuhh46o4r7knapageg7hv3vtj.apps.googleusercontent.com';

interface LocationState { from?: { pathname?: string; search?: string } }

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, verify2fa } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const googleBtnRef = useRef<HTMLDivElement>(null);
  // `||` not `??`: a present-but-EMPTY VITE_GOOGLE_CLIENT_ID must fall back to
  // the committed ID, not hide the button (?? only catches absent/undefined).
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID_FALLBACK;

  const goHome = (user?: { role?: string; is_fund_only_investor?: boolean }) => {
    const from = (location.state as LocationState | null)?.from;
    // OB.2 — managers + units-only fund investors land on /fund; SMA clients on /dashboard.
    const toFund = user?.role === 'manager' || user?.is_fund_only_investor === true;
    const home = toFund ? '/fund' : '/dashboard';
    navigate(from?.pathname ? `${from.pathname}${from.search ?? ''}` : home, { replace: true });
  };

  // Surface a failure from the Google redirect flow (GoogleLoginView redirects
  // back to /sign-in?google_error=... rather than returning JSON — see
  // apps/accounts/views.py GoogleLoginView).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const googleError = params.get('google_error');
    if (googleError) setError(googleError);
  }, [location.search]);

  useEffect(() => {
    if (!clientId || !googleBtnRef.current) return;
    const interval = setInterval(() => {
      if (typeof google === 'undefined') return;
      clearInterval(interval);
      if (!googleInitialized) {
        googleInitialized = true;
        // ux_mode: 'redirect' — Google posts the credential to login_uri as a
        // real top-level browser navigation instead of a popup + postMessage
        // handshake back to this window. The popup handshake is what Chrome's
        // default Cross-Origin-Opener-Policy intermittently blocks, leaving
        // sign-in stuck on this page with no error and no network request.
        //
        // auto_select: false — show the One-Tap prompt to a returning manager
        // with a live Google session, but require an explicit tap to confirm.
        // We deliberately do NOT zero-tap auto-sign-in: on a shared machine that
        // would silently sign in whoever last used Google (and the Google path
        // bypasses TOTP). Operator decision 2026-07-20: one-tap prompt, no
        // auto_select.
        google.accounts.id.initialize({
          client_id: clientId,
          ux_mode: 'redirect',
          login_uri: `${window.location.origin}/api/v1/auth/google`,
          auto_select: false,
        });
      }
      // Render the button FIRST — it is the guaranteed sign-in path and must
      // never be gated on the best-effort One-Tap call below.
      google.accounts.id.renderButton(googleBtnRef.current, {
        type: 'standard', theme: 'outline', size: 'large',
        text: 'signin_with', shape: 'rectangular', width: 320,
      });
      // Then surface the One-Tap prompt. Best-effort: Google applies its own
      // eligibility + cooldown rules and may suppress it, so the rendered
      // button above stays the guaranteed fallback. Fires on each mount of the
      // sign-in page (not just once per page load), so a returning manager who
      // signed out in-session is re-prompted. Wrapped in try/catch so a One-Tap
      // failure can never suppress the rendered button.
      try {
        google.accounts.id.prompt();
      } catch {
        /* One-Tap is best-effort; the rendered button is the guaranteed path. */
      }
    }, 200);
    return () => {
      clearInterval(interval);
      // Dismiss any open One-Tap prompt when leaving /sign-in so the card
      // can't linger over another route after SPA navigation.
      if (typeof google !== 'undefined') google.accounts.id.cancel();
    };
  }, [clientId]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const r = await signIn(email, password);
      if (r.twoFactorRequired) setChallenge(r.challenge ?? null);
      else if (r.user) goHome(r.user);
    } catch (err: any) {
      const detail = err?.payload?.detail;
      setError(typeof detail === 'string' ? detail : 'Sign-in failed. Check your email and password.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const u = await verify2fa(challenge!, code.trim());
      goHome(u);
    } catch (err: any) {
      const detail = err?.payload?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid code.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--paper)' }}>
      {/* minimal top bar */}
      <header style={{ padding: '24px var(--pad-x)', borderBottom: 'var(--hairline)' }}>
        <div style={{ maxWidth: 'var(--max)', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Brand onClick={() => navigate('/')} />
        </div>
      </header>

      <main
        style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'stretch', minHeight: 0 }}
        className="signin-grid"
      >
        {/* Left — editorial column */}
        <section style={{
          padding: '80px var(--pad-x)',
          borderRight: 'var(--hairline)',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        }}>
          <div>
            <Kicker>01 · Client Portal</Kicker>
            <h1 className="page-title" style={{ maxWidth: '12ch', margin: '0 0 32px' }}>
              <em>Welcome</em> back.
            </h1>
            <p className="serif-body" style={{ maxWidth: '42ch', margin: 0 }}>
              Your holdings, performance, and statements — reconciled daily and reported monthly.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginTop: 64 }}>
            <hr className="rule" />
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 24 }}>
              <span className="label">RECONCILIATION</span>
              <span className="serif-body" style={{ fontSize: 16, color: 'var(--ink-2)' }}>
                Daily, against venue records. Most recent snapshot: 00:00 UTC.
              </span>
            </div>
            <hr className="rule" />
            <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 24 }}>
              <span className="label">REPORTING</span>
              <span className="serif-body" style={{ fontSize: 16, color: 'var(--ink-2)' }}>
                Monthly statements. Immutable once issued. Corrections supersede.
              </span>
            </div>
            <hr className="rule" />
          </div>
        </section>

        {/* Right — form column */}
        <section style={{
          padding: '80px var(--pad-x)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ width: '100%', maxWidth: 420 }}>
            {challenge ? (
              <form onSubmit={submitCode} style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
                <Kicker>TWO-FACTOR · CLIENT PORTAL</Kicker>
                <p className="serif-body" style={{ margin: 0, fontSize: 15, color: 'var(--ink-2)' }}>
                  Enter the 6-digit code from your authenticator app.
                </p>
                <div className="field">
                  <label className="field-label">Authentication code</label>
                  <input className="field-input num" inputMode="numeric" autoComplete="one-time-code"
                         value={code} onChange={e => setCode(e.target.value)} required autoFocus />
                </div>
                {error && <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', fontSize: 15, color: 'var(--accent)' }}>{error}</div>}
                <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 8 }}>
                  <button type="submit" className="btn" disabled={submitting}>
                    {submitting ? 'Verifying…' : <>Verify <span className="btn-arrow">→</span></>}
                  </button>
                  <button type="button" className="btn-link" onClick={() => { setChallenge(null); setCode(''); setError(null); }}>
                    Back
                  </button>
                </div>
              </form>
            ) : (
            <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>
              <Kicker>SIGN IN · CLIENT PORTAL</Kicker>

              <div className="field">
                <label className="field-label">Email</label>
                <input
                  className="field-input"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                  required
                  autoFocus
                />
              </div>

              <div className="field">
                <label className="field-label">Password</label>
                <input
                  className="field-input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>

              {error && (
                <div style={{
                  fontFamily: 'var(--serif)', fontStyle: 'italic',
                  fontSize: 15, color: 'var(--accent)',
                }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 8 }}>
                <button type="submit" className="btn" disabled={submitting}>
                  {submitting ? 'Signing in…' : <>Continue <span className="btn-arrow">→</span></>}
                </button>
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => navigate('/reset-password')}
                >
                  Forgot your password?
                </button>
              </div>

              {clientId && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                    <div style={{ flex: 1, borderTop: 'var(--hairline)' }} />
                    <span className="label-s" style={{ color: 'var(--ink-3)' }}>OR</span>
                    <div style={{ flex: 1, borderTop: 'var(--hairline)' }} />
                  </div>
                  <div ref={googleBtnRef} />
                </div>
              )}
            </form>
            )}
          </div>
        </section>
      </main>

      <footer style={{
        padding: '20px var(--pad-x)', borderTop: 'var(--hairline)',
        fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.16em',
        textTransform: 'uppercase', color: 'var(--ink-3)',
        display: 'flex', justifyContent: 'space-between',
      }}>
        {/* "Investment adviser" label removed — LC-REV9-001 sweep (BH-RAPP-001
            pairs with it): no adviser-status holding-out until counsel confirms. */}
        <span>Rev9 Capital · Secured session</span>
        <span>Miami, FL</span>
      </footer>
      <div style={{ padding: '0 var(--pad-x) 20px' }}>
        <EntityDisclaimer style={{ margin: 0 }} />
      </div>

      <style>{`
        @media (max-width: 900px) {
          .signin-grid { grid-template-columns: 1fr !important; }
          .signin-grid > section:first-child { border-right: 0; border-bottom: var(--hairline); }
        }
      `}</style>
    </div>
  );
}
